/**
 * Site Refresh 2026: "Crafted by Hand, Built to Last" gallery (A8, Build-Spec §8.1; A73 rows and loading).
 *
 * Items: the photos in the HTML (A73: the first 12 of the shared list) plus, when the section has a
 * data-rg-source (the TWH Tools route /apps/custom-order/gallery), the rest of the running order,
 * fetched 24 at a time: page 1 when the gallery comes within a screen of the viewport or the viewer
 * opens, further pages as the rows near the end of what they hold (at most ~60 tiles in the rows) or
 * as the viewer moves on. A failed or slow (> 4 s) request changes nothing: the HTML photos stay.
 *
 * Rows (A73): each row is a track moved by a transform, one offset per row, drawn every frame
 * while something moves. The JS repeats the first tiles after the last ones, so the row loops
 * without an end in both directions. Drift: row 1 left, row 2 right, ~30px/s, easing in and out.
 * The visitor moves them with the Previous / Next arrows (about one screen, smooth; instant with
 * reduced motion), mouse drag and touch swipe (with a little momentum; a drag never opens the
 * viewer, a tap does), sideways trackpad scroll and Left / Right on a focused tile (one tab stop
 * per row). Drift stops while the visitor drags or swipes, on an arrow press or sideways scroll, and
 * resumes 5s after the last of these, unless Pause is on (A73b, Ray 26 Oct: a mouse resting on the
 * photos no longer holds them still). Keyboard focus on a tile holds the rows while it stays there;
 * closing the viewer counts as an interaction (5s, then drift), not as a hold. Off-screen: still.
 * prefers-reduced-motion: no drift, no momentum.
 * A73c: the Pause / Play button shows what the rows do: "Play" (pressed) while they stand still for any
 * reason (Pause, a drag, keyboard focus), "Pause" while they drift. Play restarts them at once.
 *
 * Viewer: full screen, the whole photo (object-fit: contain), counter over every item (photos and
 * videos), Close, prev/next, arrow keys, swipe, thumbnails that keep their shape, Esc, focus trapped,
 * focus returned. Video items (product page) show a <video> (controls, MP4 set only when shown);
 * moving on, closing or Esc pauses it.
 */
(() => {
	if (window.__rgInit) return;
	window.__rgInit = true;

	const SPEED = 30; // drift, px per second
	const IDLE_MS = 5000; // drift resumes this long after the last interaction
	const EASE_MS = 700; // drift slows to a stop / speeds back up over this time
	const ARROW_MS = 650;
	const PER = 24;
	const TIMEOUT_MS = 4000;
	const ROW_MAX = 60; // tiles in the two rows together (the viewer has everything)
	const DRAG_PX = 6;
	const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
	const phone = window.matchMedia('(max-width: 767px)');

	const mod = (a, n) => ((a % n) + n) % n;
	const keyboardFocus = (el) => { try { return el.matches(':focus-visible'); } catch (e) { return true; } };
	const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
	const cdn = (url, k, v) => url + (url.indexOf('?') < 0 ? '?' : '&') + k + '=' + v;

	/* ------------------------------------------------------------- items */

	/* one item of the route answer → the viewer's shape */
	function fromRoute(it) {
		const r = it.width && it.height ? it.width / it.height : 1;
		if (it.type === 'video') {
			return { type: 'video', src: it.mp4 || '', poster: cdn(it.poster || it.src, 'width', 1600), base: it.poster || it.src, thumb: cdn(it.poster || it.src, 'height', 128), alt: it.alt || '', r };
		}
		const s = it.src;
		return {
			type: 'image', base: s, src: cdn(s, 'width', 1600), alt: it.alt || '', r,
			srcset: [800, 1200, 1600, 2048].map((w) => cdn(s, 'width', w) + ' ' + w + 'w').join(', '),
			thumb: cdn(s, 'height', 128),
		};
	}

	function makeStore(root) {
		let html = [];
		try { html = JSON.parse(root.querySelector('[data-rg-data]').textContent) || []; } catch (e) { html = []; }
		const source = root.getAttribute('data-rg-source') || '';
		const s = {
			items: html.slice(), // sparse once pages arrive out of order (viewer: Previous from the first item)
			base: html.length,
			total: html.length,
			videos: 0,
			pages: source ? Infinity : 0,
			known: !source,
			loaded: {},
			inflight: {},
			listeners: [],
			dead: !source,
		};

		s.page = (p) => {
			if (s.dead || p < 1 || p > s.pages) return Promise.resolve(false);
			if (s.loaded[p]) return Promise.resolve(true);
			if (s.inflight[p]) return s.inflight[p];
			const ctl = 'AbortController' in window ? new AbortController() : null;
			const timer = setTimeout(() => ctl && ctl.abort(), TIMEOUT_MS);
			const url = source + (source.indexOf('?') < 0 ? '?' : '&') + 'page=' + p + '&per=' + PER;
			const req = fetch(url, { credentials: 'same-origin', headers: { accept: 'application/json' }, signal: ctl ? ctl.signal : undefined })
				.then((r) => (r.ok ? r.json() : null))
				.then((d) => {
					clearTimeout(timer);
					if (!d || !d.ok || !Array.isArray(d.items)) { if (!s.known) s.dead = true; return false; }
					s.total = Math.max(s.base, d.total | 0);
					s.videos = d.videos | 0;
					s.pages = d.pages | 0;
					s.known = true;
					const start = s.base + (p - 1) * PER;
					d.items.forEach((it, k) => { s.items[start + k] = fromRoute(it); });
					s.loaded[p] = true;
					s.listeners.forEach((fn) => fn(start, start + d.items.length));
					return true;
				})
				.catch(() => { clearTimeout(timer); if (!s.known) s.dead = true; return false; })
				.then((ok) => { delete s.inflight[p]; return ok; });
			s.inflight[p] = req;
			return req;
		};

		/* the page holding item i (nothing to fetch for the HTML photos) */
		s.ensure = (i) => (i < s.base || s.items[i] ? Promise.resolve(true) : s.page(Math.floor((i - s.base) / PER) + 1));
		s.on = (fn) => s.listeners.push(fn);
		return s;
	}

	/* ------------------------------------------------------------- rows */

	function initSection(root) {
		if (root.__rg) return;
		root.__rg = true;
		const store = makeStore(root);
		root.__rgStore = store;
		const pauseBtn = root.querySelector('.js-rg-pause');
		const pauseLabel = root.querySelector('.js-rg-pause-label');
		const rowsEl = root.querySelector('.rg__rows');
		// A17: section setting "Start paused" renders data-rg-start-paused (button already pressed, "Play")
		let paused = root.hasAttribute('data-rg-start-paused');
		let visible = !('IntersectionObserver' in window);
		let focusIn = false;
		let returning = false; // the viewer is closing and hands focus back to a tile
		let lastInput = -Infinity;
		let raf = 0;
		let last = 0;
		let idleTimer = 0;
		let inRows = 0; // items placed in the rows (index 0..inRows-1)

		const rows = [...root.querySelectorAll('.js-rg-row')].map((el) => ({
			el,
			track: el.querySelector('.rg__track'),
			dir: parseFloat(el.getAttribute('data-rg-dir')) || 1,
			h: parseFloat(el.getAttribute('data-rg-h')) || 300,
			hPhone: parseFloat(el.getAttribute('data-rg-h-phone')) || 200,
			offset: 0,
			loop: 0,
			width: 0,
			rate: 0,
			vel: 0,
			tween: null,
			drag: null,
			pending: [],
			moved: 1,
		}));
		rows.forEach((r) => { r.track.querySelectorAll('.rg__tile').forEach(() => { inRows++; }); });

		const originals = (r) => [...r.track.children].filter((t) => !t.classList.contains('rg__tile--repeat'));

		/* a repeat tile; its images are rebuilt with loading="lazy" set before src (a cloned <img> copies src
		   first and would download at once, off-screen) */
		function copyTile(tile) {
			const c = tile.cloneNode(false);
			[...tile.childNodes].forEach((n) => {
				if (n.nodeName !== 'IMG') { c.appendChild(n.cloneNode(true)); return; }
				const img = document.createElement('img');
				img.loading = 'lazy';
				[...n.attributes].forEach((a) => { if (a.name !== 'loading') img.setAttribute(a.name, a.value); });
				c.appendChild(img);
			});
			return c;
		}

		/* the repeat tiles: copies of the first tiles, enough to fill the row after the last one */
		function measure(r) {
			r.track.querySelectorAll('.rg__tile--repeat').forEach((c) => c.remove());
			const tiles = originals(r);
			r.width = r.el.clientWidth;
			if (!tiles.length) { r.loop = 0; return; }
			const first = tiles[0];
			const end = tiles[tiles.length - 1];
			const span = end.offsetLeft + end.offsetWidth - first.offsetLeft;
			if (span <= 0) { r.loop = 0; return; }
			let covered = 0;
			let k = 0;
			const frag = document.createDocumentFragment();
			while (covered < r.width + 400 && k < tiles.length * 20) {
				const src = tiles[k % tiles.length];
				const c = copyTile(src);
				c.classList.add('rg__tile--repeat');
				c.setAttribute('tabindex', '-1');
				c.setAttribute('aria-hidden', 'true');
				frag.appendChild(c);
				covered += src.offsetWidth + 12;
				k++;
			}
			r.track.appendChild(frag);
			const firstCopy = r.track.querySelector('.rg__tile--repeat');
			const oldLoop = r.loop;
			r.loop = firstCopy ? firstCopy.offsetLeft - first.offsetLeft : 0;
			if (oldLoop && r.loop && oldLoop !== r.loop && !r.drag && !r.tween) r.offset = mod(r.offset, r.loop);
			draw(r);
		}

		function draw(r) {
			if (!r.loop) { r.track.style.transform = ''; return; }
			r.track.style.transform = 'translate3d(' + (-mod(r.offset, r.loop)).toFixed(2) + 'px,0,0)';
		}

		/* -------- new tiles from the route (appended before the repeat tiles, when the row shows none of them) */

		function tileFor(item, index, r) {
			const b = document.createElement('button');
			b.type = 'button';
			b.className = 'rg__tile js-rg-open' + (item.type === 'video' ? ' rg__tile--video' : '');
			b.setAttribute('data-rg-index', String(index));
			b.setAttribute('aria-haspopup', 'dialog');
			b.setAttribute('tabindex', '-1');
			if (item.type === 'video') b.setAttribute('aria-label', item.alt || root.dataset.rgPlayVideo || '');
			else if (!item.alt) b.setAttribute('aria-label', root.dataset.rgZoom || '');
			const ratio = item.r || 1;
			const wDesk = Math.round(r.h * ratio);
			const wPhone = Math.round(r.hPhone * ratio);
			const w = (h) => Math.round(h * ratio);
			const url = (h) => cdn(item.base, 'width', w(h));
			if (item.type === 'video') b.insertAdjacentHTML('beforeend', '<span class="rg__play" aria-hidden="true"><svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" focusable="false"><path d="M8 5v14l11-7z"/></svg></span>');
			const img = document.createElement('img');
			img.loading = 'lazy'; // before src: nothing downloads until the tile nears the screen
			img.decoding = 'async';
			img.draggable = false;
			img.width = wDesk;
			img.height = r.h;
			img.alt = item.type === 'video' ? '' : item.alt || '';
			img.sizes = '(max-width: 767px) ' + wPhone + 'px, ' + wDesk + 'px';
			img.srcset = url(r.hPhone) + ' ' + w(r.hPhone) + 'w, ' + url(r.h) + ' ' + w(r.h) + 'w, ' + url(r.h * 2) + ' ' + w(r.h * 2) + 'w';
			img.src = url(r.h);
			b.appendChild(img);
			return b;
		}

		/* rows fill in order only (a page the viewer fetched further on waits for the ones before it) */
		function placeNew() {
			while (inRows < ROW_MAX && store.items[inRows]) {
				const r = rows[inRows % 2] || rows[0];
				r.pending.push(tileFor(store.items[inRows], inRows, r));
				inRows++;
			}
			rows.forEach((r) => flush(r, false));
		}

		/* safe = the row shows no repeat tile right now (or nobody sees it), so nothing visible jumps; the
		   visitor moving the row (arrow, drag, wheel) also lets them in, as everything moves anyway */
		function flush(r, force) {
			if (!r.pending.length) return;
			const o = r.loop ? mod(r.offset, r.loop) : 0;
			const safe = !visible || !r.loop || (o + r.width <= r.loop - 12 && !r.drag && !r.tween);
			if (!safe && !force) return;
			const firstCopy = r.track.querySelector('.rg__tile--repeat');
			const frag = document.createDocumentFragment();
			r.pending.forEach((t) => frag.appendChild(t));
			r.pending = [];
			r.track.insertBefore(frag, firstCopy);
			r.offset = o;
			measure(r);
		}

		store.on(placeNew);

		/* -------- the "View all" label, once the count is known */

		function label() {
			if (!store.known || !root.hasAttribute('data-rg-source')) return;
			const media = root.hasAttribute('data-rg-videos') && store.videos > 0;
			const t = (media ? root.dataset.rgAllMedia : root.dataset.rgAll) || '';
			if (!t) return;
			root.querySelectorAll('.js-rg-all').forEach((b) => { b.textContent = t.replace('__N__', String(store.total)); });
		}
		store.on(label);

		/* rows near their end → next page (only while the rows hold fewer than ROW_MAX) */
		function wantMore(r) {
			if (inRows >= ROW_MAX || store.dead || !store.known || !r.loop) return;
			const o = mod(r.offset, r.loop);
			const nearEnd = r.moved > 0 ? r.loop - (o + r.width) < r.width : o < r.width;
			if (!nearEnd) return;
			const next = Math.floor((inRows - store.base) / PER) + 1;
			if (next >= 1 && next <= store.pages) store.page(next);
		}

		/* -------- motion */

		function drifting() {
			return !paused && !reduced.matches && visible && !focusIn && performance.now() - lastInput >= IDLE_MS;
		}

		/* the button: pressed + "Play" while the rows are held still, "Pause" while they drift (off-screen
		   does not count: the rows would drift if they were on screen) */
		let shownPaused = null;
		function held() {
			return paused || (!reduced.matches && (focusIn || performance.now() - lastInput < IDLE_MS));
		}
		function syncButton() {
			if (!pauseBtn) return;
			const p = held();
			if (p === shownPaused) return;
			shownPaused = p;
			pauseBtn.setAttribute('aria-pressed', p ? 'true' : 'false');
			if (pauseLabel) pauseLabel.textContent = p ? root.dataset.rgPlay : root.dataset.rgPause;
		}

		function touch() {
			lastInput = performance.now();
			clearTimeout(idleTimer);
			idleTimer = setTimeout(kick, IDLE_MS + 30);
			kick();
		}

		function kick() { syncButton(); if (!raf) { last = 0; raf = requestAnimationFrame(frame); } }

		function frame(now) {
			raf = 0;
			syncButton();
			const dt = last ? Math.min(now - last, 50) / 1000 : 1 / 60;
			last = now;
			const go = drifting();
			let more = false;
			rows.forEach((r) => {
				if (!r.loop) return;
				const before = r.offset;
				if (r.drag) {
					r.rate = 0;
				} else if (r.tween) {
					const t = Math.min((now - r.tween.t0) / r.tween.dur, 1);
					r.offset = r.tween.from + (r.tween.to - r.tween.from) * ease(t);
					if (t >= 1) { r.tween = null; r.offset = mod(r.offset, r.loop); }
					more = true;
				} else if (Math.abs(r.vel) > 8) {
					r.offset += r.vel * dt;
					r.vel *= Math.exp(-dt / 0.2);
					more = true;
				} else {
					r.vel = 0;
					const target = go ? 1 : 0;
					if (r.rate !== target) {
						const step = (dt * 1000) / EASE_MS;
						r.rate = target > r.rate ? Math.min(target, r.rate + step) : Math.max(target, r.rate - step);
					}
					const k = r.rate * r.rate * (3 - 2 * r.rate); // smoothstep: no sudden start or stop
					if (k > 0) r.offset += r.dir * SPEED * k * dt;
					if (r.rate > 0 || target > 0) more = true;
				}
				if (r.offset !== before) r.moved = r.offset > before ? 1 : -1;
				draw(r);
				flush(r, false);
				wantMore(r);
			});
			if (more || rows.some((r) => r.drag)) raf = requestAnimationFrame(frame);
		}

		/* arrows: both rows about one screen, smooth (instant with reduced motion) */
		function step(sign) {
			touch();
			rows.forEach((r) => {
				flush(r, true);
				if (!r.loop) return;
				const dist = Math.max(r.width * 0.85, 200) * sign;
				r.vel = 0;
				r.rate = 0;
				if (reduced.matches) { r.offset = mod(r.offset + dist, r.loop); r.moved = sign; draw(r); return; }
				const from = r.tween ? r.tween.to : r.offset;
				r.tween = { from: r.offset, to: from + dist, t0: performance.now(), dur: ARROW_MS };
			});
			kick();
		}
		root.querySelectorAll('.js-rg-prev').forEach((b) => b.addEventListener('click', () => step(-1)));
		root.querySelectorAll('.js-rg-next').forEach((b) => b.addEventListener('click', () => step(1)));

		/* bring a focused tile fully into view (keyboard). It must be the tile itself, not its repeat copy,
		   so the offset stays inside 0..loop, where the originals are drawn */
		function reveal(r, tile, smooth) {
			if (!r.loop) return;
			const first = originals(r)[0];
			const x = tile.offsetLeft - first.offsetLeft;
			const w = tile.offsetWidth;
			const pad = Math.min(120, r.width * 0.1);
			const o = mod(r.offset, r.loop);
			let to = o;
			if (x - o < pad) to = x - pad;
			else if (x + w - o > r.width - pad) to = x + w - r.width + pad;
			to = Math.min(Math.max(to, 0), r.loop - 1);
			if (Math.abs(to - o) < 1) return;
			r.vel = 0;
			if (!smooth || reduced.matches) { r.tween = null; r.offset = to; draw(r); return; }
			r.offset = o;
			r.tween = { from: o, to, t0: performance.now(), dur: 400 };
			kick();
		}

		rows.forEach((r) => {
			// drag (mouse) and swipe (touch, pen): the row follows the pointer; a vertical touch scrolls the page
			r.el.addEventListener('pointerdown', (e) => {
				if (e.pointerType === 'mouse' && e.button !== 0) return;
				r.drag = null;
				r.start = { id: e.pointerId, x: e.clientX, y: e.clientY, offset: r.offset, samples: [[performance.now(), e.clientX]], active: false };
			});
			r.el.addEventListener('pointermove', (e) => {
				const s = r.start;
				if (!s || s.id !== e.pointerId) return;
				const dx = e.clientX - s.x;
				if (!s.active) {
					if (Math.abs(dx) < DRAG_PX || Math.abs(dx) < Math.abs(e.clientY - s.y)) return;
					s.active = true;
					flush(r, true);
					s.x = e.clientX;
					s.offset = r.offset;
					r.tween = null;
					r.vel = 0;
					r.drag = s;
					try { r.el.setPointerCapture(e.pointerId); } catch (err) { /* already released */ }
					r.el.classList.add('is-dragging');
					touch();
				}
				r.offset = s.offset - (e.clientX - s.x);
				s.samples.push([performance.now(), e.clientX]);
				if (s.samples.length > 6) s.samples.shift();
				draw(r);
			});
			const end = (e) => {
				const s = r.start;
				if (!s || s.id !== e.pointerId) return;
				r.start = null;
				if (!s.active) return;
				r.drag = null;
				r.el.classList.remove('is-dragging');
				r.justDragged = true;
				setTimeout(() => { r.justDragged = false; }, 350);
				const a = s.samples[0];
				const b = s.samples[s.samples.length - 1];
				const dt = (b[0] - a[0]) / 1000;
				const fresh = performance.now() - b[0] < 80;
				r.vel = !reduced.matches && fresh && dt > 0 ? Math.max(-1500, Math.min(1500, -(b[1] - a[1]) / dt)) : 0;
				r.offset = mod(r.offset, r.loop || 1);
				touch();
			};
			r.el.addEventListener('pointerup', end);
			r.el.addEventListener('pointercancel', (e) => { end(e); r.justDragged = false; });
			// a drag never opens the viewer
			r.el.addEventListener('click', (e) => {
				if (r.justDragged) { e.preventDefault(); e.stopPropagation(); r.justDragged = false; }
			}, true);
			// trackpad sideways / shift+wheel
			r.el.addEventListener('wheel', (e) => {
				const sideways = Math.abs(e.deltaX) > Math.abs(e.deltaY);
				if (!sideways && !e.shiftKey) return;
				e.preventDefault();
				flush(r, true);
				const d = (sideways ? e.deltaX : e.deltaY) * (e.deltaMode === 1 ? 16 : 1);
				r.tween = null;
				r.vel = 0;
				r.offset += d;
				r.moved = d > 0 ? 1 : -1;
				draw(r);
				touch();
			}, { passive: false });
			// keyboard: one tab stop per row, Left / Right to the neighbour
			r.el.addEventListener('keydown', (e) => {
				if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
				const tile = e.target.closest && e.target.closest('.rg__tile');
				if (!tile || tile.classList.contains('rg__tile--repeat')) return;
				const list = originals(r);
				const k = list.indexOf(tile);
				if (k < 0) return;
				e.preventDefault();
				const next = list[mod(k + (e.key === 'ArrowRight' ? 1 : -1), list.length)];
				tile.setAttribute('tabindex', '-1');
				next.setAttribute('tabindex', '0');
				next.focus({ preventScroll: true });
				r.moved = e.key === 'ArrowRight' ? 1 : -1;
				reveal(r, next, true);
				touch();
			});
			r.el.addEventListener('focusin', (e) => {
				const tile = e.target.closest && e.target.closest('.rg__tile');
				r.el.scrollLeft = 0; // a hidden-overflow row must never scroll itself
				if (tile && !tile.classList.contains('rg__tile--repeat')) {
					originals(r).forEach((t) => t.setAttribute('tabindex', t === tile ? '0' : '-1'));
					if (keyboardFocus(e.target)) reveal(r, tile, false);
				}
			});
		});

		// one tab stop per row: the first tile, unless one was focused before
		rows.forEach((r) => {
			const list = originals(r);
			if (list.length && !list.some((t) => t.getAttribute('tabindex') === '0')) list.forEach((t, k) => t.setAttribute('tabindex', k ? '-1' : '0'));
		});

		if (rowsEl) {
			// keyboard focus only: a tile a mouse pressed (or dragged) keeps focus but lets the rows drift on,
			// and so does the tile the viewer hands focus back to when it closes
			rowsEl.addEventListener('focusin', (e) => {
				focusIn = !returning && keyboardFocus(e.target);
				if (focusIn) kick(); else touch();
			});
			// A73d: only a keyboard hold ending counts as an interaction. A tile the mouse pressed (a drag
			// focuses it) losing focus to the Pause button must not hold the rows: that turned the click on
			// "Pause" into a "Play" (the rows looked held for an instant) and the first click did nothing.
			rowsEl.addEventListener('focusout', (e) => {
				if (rowsEl.contains(e.relatedTarget)) return;
				const wasHeld = focusIn;
				focusIn = false;
				if (wasHeld) touch(); else kick();
			});
		}

		if (pauseBtn) {
			pauseBtn.addEventListener('click', () => {
				// "Play" (held for any reason): drift now; "Pause": stop for good
				if (held()) {
					paused = false;
					focusIn = false;
					lastInput = -Infinity;
					clearTimeout(idleTimer);
				} else {
					paused = true;
				}
				kick();
			});
		}

		if ('IntersectionObserver' in window) {
			// motion only while on screen
			new IntersectionObserver((entries) => {
				visible = entries.some((e) => e.isIntersecting);
				if (visible) { rows.forEach((r) => { r.rate = 0; }); kick(); }
				else rows.forEach((r) => flush(r, true));
			}, { rootMargin: '100px 0px' }).observe(rowsEl || root);
			// the first page, about one screen ahead
			const ahead = new IntersectionObserver((entries) => {
				if (!entries.some((e) => e.isIntersecting)) return;
				ahead.disconnect();
				store.page(1);
			}, { rootMargin: '100% 0px' });
			ahead.observe(root);
		} else {
			store.page(1);
		}

		reduced.addEventListener && reduced.addEventListener('change', () => { rows.forEach((r) => { r.vel = 0; r.tween = null; }); kick(); });
		let resizeTimer = 0;
		window.addEventListener('resize', () => {
			clearTimeout(resizeTimer);
			resizeTimer = setTimeout(() => rows.forEach(measure), 150);
		});
		window.addEventListener('load', () => { rows.forEach(measure); kick(); });
		// images get their size from width/height attributes, so the loop width is known now
		rows.forEach(measure);
		kick();

		root.addEventListener('rg:viewer-close', () => {
			returning = true;
			setTimeout(() => { returning = false; }, 0);
			touch();
		});

		root.__rgState = { rows, store, get paused() { return paused; }, get visible() { return visible; }, get drifting() { return drifting(); } };

		root.addEventListener('click', (e) => {
			const opener = e.target.closest('.js-rg-open');
			if (!opener || !root.contains(opener)) return;
			const index = parseInt(opener.getAttribute('data-rg-index'), 10) || 0;
			openViewer(root, index, opener);
		});
	}

	/* ------------------------------------------------------------- viewer */

	let viewer = null;

	function icon(path, stroke) {
		return '<svg width="' + (stroke ? 18 : 14) + '" height="' + (stroke ? 18 : 14) + '" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="' + (stroke ? 2 : 2.4) + '" aria-hidden="true" focusable="false"><path d="' + path + '"/></svg>';
	}

	function build(root) {
		const store = root.__rgStore;
		const v = document.createElement('div');
		v.className = 'rg-viewer';
		v.hidden = true;
		v.setAttribute('role', 'dialog');
		v.setAttribute('aria-modal', 'true');
		v.setAttribute('aria-label', root.dataset.rgTitle || '');
		v.innerHTML =
			'<p class="rg-viewer__counter" aria-live="polite"></p>' +
			'<button type="button" class="rg-viewer__close">' + icon('M6 6l12 12M18 6L6 18') + '</button>' +
			'<button type="button" class="rg-viewer__nav rg-viewer__nav--prev">' + icon('M15 6l-6 6 6 6', true) + '</button>' +
			'<button type="button" class="rg-viewer__nav rg-viewer__nav--next">' + icon('M9 6l6 6-6 6', true) + '</button>' +
			'<div class="rg-viewer__stage"><img class="rg-viewer__img" alt="" decoding="async">' +
			'<video class="rg-viewer__video" controls playsinline preload="none" hidden></video></div>' +
			'<div class="rg-viewer__thumbs"></div>';
		v.querySelector('.rg-viewer__close').setAttribute('aria-label', root.dataset.rgClose || 'Close');
		v.querySelector('.rg-viewer__nav--prev').setAttribute('aria-label', root.dataset.rgPrev || 'Previous photo');
		v.querySelector('.rg-viewer__nav--next').setAttribute('aria-label', root.dataset.rgNext || 'Next photo');
		document.body.appendChild(v);

		const thumbs = v.querySelector('.rg-viewer__thumbs');
		const video = v.querySelector('.rg-viewer__video');
		let img = v.querySelector('.rg-viewer__img');
		const stage = v.querySelector('.rg-viewer__stage');
		const state = { v, root, index: 0, opener: null, want: -1 };

		/* one thumbnail per item of the whole order; an empty one until its page arrives */
		function fillThumb(b, i) {
			const p = store.items[i];
			b.setAttribute('aria-label', (i + 1) + ' / ' + store.total);
			if (!p || b.__filled) return;
			b.__filled = true;
			b.classList.remove('rg-viewer__thumb--empty');
			const t = document.createElement('img');
			t.alt = '';
			t.loading = 'lazy';
			t.decoding = 'async';
			t.src = p.thumb;
			t.width = Math.round(64 * (p.r || 1));
			t.height = 64;
			b.appendChild(t);
			if (p.type === 'video') {
				b.classList.add('rg-viewer__thumb--video');
				b.insertAdjacentHTML('beforeend', '<span class="rg-viewer__thumb-play" aria-hidden="true"><svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" focusable="false"><path d="M8 5v14l11-7z"/></svg></span>');
			}
		}

		function syncThumbs() {
			for (let i = thumbs.children.length; i < store.total; i++) {
				const b = document.createElement('button');
				b.type = 'button';
				b.className = 'rg-viewer__thumb rg-viewer__thumb--empty';
				b.addEventListener('click', () => show(i, true));
				thumbs.appendChild(b);
			}
			[...thumbs.children].forEach(fillThumb);
		}

		function stopVideo() {
			if (!video.paused) video.pause();
		}

		/* a video item shows the <video>; its MP4 is set only now (none in the rows), and it plays when
		   the shopper opened it with a click (tile or thumbnail; sound allowed) */
		/* the photo's box inside the stage (its own shape, as large as fits), so the quick picture and
		   the sharp one sit in the same place */
		function fit(el, r) {
			const w = stage.clientWidth, h = stage.clientHeight;
			if (!w || !h) return;
			let bw = w, bh = w / (r || 1);
			if (bh > h) { bh = h; bw = h * (r || 1); }
			el.style.width = Math.round(bw) + 'px';
			el.style.height = Math.round(bh) + 'px';
		}

		/* A73c: every photo gets a new <img>. Re-using one made the browser keep the previous photo on
		   screen until the next one had downloaded (1-2 s). The new one shows at once the picture the
		   rows already loaded for it (or the thumbnail) and turns sharp when the full size is in. */
		function photo(p, at) {
			const el = document.createElement('img');
			el.className = 'rg-viewer__img';
			el.alt = p ? p.alt || '' : '';
			el.decoding = 'async';
			if (p) {
				const tile = root.querySelector('.rg__tile[data-rg-index="' + at + '"] img');
				const quick = tile && tile.complete && tile.naturalWidth ? tile.currentSrc : p.thumb;
				if (quick) el.style.backgroundImage = 'url("' + quick + '")';
				fit(el, p.r);
				el.addEventListener('load', () => { el.style.backgroundImage = ''; }, { once: true });
				el.sizes = '100vw';
				el.srcset = p.srcset || '';
				el.src = p.src;
			} else {
				el.hidden = true; // its page is still on the way
			}
			img.replaceWith(el);
			img = el;
		}

		function render(play) {
			const n = store.total;
			const p = store.items[state.index];
			v.querySelector('.rg-viewer__counter').textContent = (state.index + 1) + ' / ' + n;
			thumbs.querySelectorAll('.rg-viewer__thumb').forEach((t, k) => t.setAttribute('aria-current', k === state.index ? 'true' : 'false'));
			const cur = thumbs.children[state.index];
			if (cur) cur.scrollIntoView({ block: 'nearest', inline: 'center' });
			stopVideo();
			if (!p) { video.hidden = true; photo(null, state.index); return; }
			if (p.type === 'video') {
				img.hidden = true;
				img.removeAttribute('srcset');
				img.removeAttribute('src');
				video.hidden = false;
				video.poster = p.poster || '';
				if (video.getAttribute('src') !== p.src) video.src = p.src;
				video.setAttribute('aria-label', p.alt || root.dataset.rgPlayVideo || '');
				if (play) {
					const pr = video.play();
					if (pr && pr.catch) pr.catch(() => {});
				}
			} else {
				video.hidden = true;
				if (video.getAttribute('src')) { video.removeAttribute('src'); video.load(); }
				photo(p, state.index);
			}
			[1, -1].forEach((d) => {
				const q = store.items[mod(state.index + d, n)];
				if (!q) return;
				const pre = new Image();
				if (q.type === 'video') { if (q.poster) pre.src = q.poster; return; }
				pre.srcset = q.srcset || ''; pre.sizes = '100vw'; pre.src = q.src;
			});
		}

		function show(i, play) {
			const n = store.total;
			if (!n) return;
			state.index = mod(i, n);
			state.want = state.index;
			const at = state.index;
			stopVideo();
			render(play);
			if (!store.items[at]) {
				store.ensure(at).then(() => { if (state.want === at && !v.hidden) render(play); });
			}
			// the next page before it is needed, either way round
			store.ensure(mod(at + 3, n));
			store.ensure(mod(at - 3, n));
		}

		store.on(() => {
			syncThumbs();
			if (!v.hidden) v.querySelector('.rg-viewer__counter').textContent = (state.index + 1) + ' / ' + store.total;
		});
		syncThumbs();

		v.querySelector('.rg-viewer__close').addEventListener('click', close);
		v.querySelector('.rg-viewer__nav--prev').addEventListener('click', () => show(state.index - 1));
		v.querySelector('.rg-viewer__nav--next').addEventListener('click', () => show(state.index + 1));

		v.addEventListener('keydown', (e) => {
			if (e.key === 'Escape') { e.preventDefault(); close(); return; }
			if (e.target === video && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')) return; // seek, as native
			if (e.key === 'ArrowRight') { e.preventDefault(); show(state.index + 1); return; }
			if (e.key === 'ArrowLeft') { e.preventDefault(); show(state.index - 1); return; }
			if (e.key !== 'Tab') return;
			const items = [...v.querySelectorAll('button, video')].filter((b) => b.offsetParent !== null);
			if (!items.length) return;
			const first = items[0], lastEl = items[items.length - 1];
			if (e.shiftKey && document.activeElement === first) { e.preventDefault(); lastEl.focus(); }
			else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); first.focus(); }
		});

		// swipe (one finger, not while pinch-zoomed)
		let sx = null, sy = null;
		stage.addEventListener('touchstart', (e) => {
			if (e.touches.length !== 1) { sx = null; return; }
			sx = e.touches[0].clientX; sy = e.touches[0].clientY;
		}, { passive: true });
		stage.addEventListener('touchend', (e) => {
			if (sx === null) return;
			const t = e.changedTouches[0];
			const dx = t.clientX - sx, dy = t.clientY - sy;
			sx = null;
			const zoomed = window.visualViewport && window.visualViewport.scale > 1.05;
			if (!zoomed && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) show(state.index + (dx < 0 ? 1 : -1));
		}, { passive: true });

		function close() {
			stopVideo();
			state.want = -1;
			v.hidden = true;
			window.removeEventListener('resize', refit);
			document.documentElement.classList.remove('rg-locked');
			root.dispatchEvent(new CustomEvent('rg:viewer-close'));
			if (state.opener && document.contains(state.opener)) state.opener.focus();
		}

		function refit() {
			const p = store.items[state.index];
			if (p && p.type !== 'video' && !img.hidden) fit(img, p.r);
		}

		state.show = show;
		state.close = close;
		state.refit = refit;
		return state;
	}

	function openViewer(root, index, opener) {
		if (!viewer || viewer.root !== root) {
			if (viewer) viewer.v.remove();
			viewer = build(root);
		}
		const store = root.__rgStore;
		if (!store.known && !store.dead) store.page(1);
		viewer.opener = opener;
		viewer.v.hidden = false;
		window.addEventListener('resize', viewer.refit);
		document.documentElement.classList.add('rg-locked');
		viewer.show(index, true);
		viewer.v.querySelector('.rg-viewer__close').focus();
	}

	function init() { document.querySelectorAll('[data-rg]').forEach(initSection); }
	if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
	else init();
	document.addEventListener('shopify:section:load', init);
})();
